import { tt } from './i18n'

/**
 * A support ticket's vocabulary - `FRONTEND.md` §9.510, `BACKEND.md` §10A.265, owner O31.
 *
 * **The owner asked for this half by name**: *"shared packages which can be used for manager/SaaS to share ticket
 * system things"*. `@agos/shared` is already a dependency of both apps, so this is where it goes.
 *
 * **The server is the authority and this is not.** `SupportTicketRules.mayTransition` refuses an illegal move
 * whatever a screen draws; what is here decides which buttons to offer, so an operator is not shown one that would
 * be refused. A client-side copy of a server rule is how `agos-a-server-fix-leaves-the-clients-copy-behind`
 * happens, and `TicketVocabularyAgreesTest` on the backend is what stops it - the drift is invisible to both
 * sides' own tests, because each is right about itself.
 */

export const TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_on_franchise',
  'resolved',
  'closed',
] as const

export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_CATEGORIES = [
  'bug',
  'question',
  'feature',
  'billing',
  'hardware',
  'other',
] as const

export type TicketCategory = (typeof TICKET_CATEGORIES)[number]

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const

export type TicketPriority = (typeof TICKET_PRIORITIES)[number]

/** The same two caps the server enforces, so a long report cannot be typed and then lost. */
export const TICKET_SUBJECT_MAX = 160
export const TICKET_BODY_MAX = 8_000

/**
 * What a status is called, and it depends on whose screen it is.
 *
 * **`waiting_on_franchise` is why this takes an argument.** That status exists to say *whose turn is it*, so the
 * franchise reads *Čaká na vás* and the platform reads *Čaká na franšízu*. One label map would tell somebody they
 * are waiting for themselves, which is worse than no label at all.
 */
export function ticketStatusLabel(status: TicketStatus, viewer: 'franchise' | 'platform'): string {
  switch (status) {
    case 'open':
      return tt('Nová')
    case 'in_progress':
      return tt('Riešime')
    case 'waiting_on_franchise':
      return viewer === 'franchise' ? tt('Čaká na vás') : tt('Čaká na franšízu')
    case 'resolved':
      return tt('Vyriešená')
    case 'closed':
      return tt('Uzavretá')
  }
}

/**
 * The colour a status wears - §3.139.
 *
 * **Both ticket screens drew the status in a pill with `border-border` and no colour at all**, so an open
 * ticket looked exactly like a closed one on the queue that exists to be triaged. §3.96 found the same
 * thing on the packages screen and its reasoning applies here: a state whose colour says nothing is a
 * state somebody has to read.
 *
 * **Exhaustive over the union with no `default`**, so a sixth status is a compile error rather than a
 * silently grey badge - the same construction §9.507 used for the map apps, and the reason §9.512's fifth
 * option could not ship as a wrong link.
 *
 * The two that need doing something about are warm, the two that are finished are cool, and *Riešime* is
 * blue because it is the platform's own work in progress rather than anybody's queue.
 */
export function ticketStatusTone(status: TicketStatus): 'amber' | 'blue' | 'green' | 'zinc' {
  switch (status) {
    case 'open':
      return 'amber'
    case 'in_progress':
      return 'blue'
    case 'waiting_on_franchise':
      return 'amber'
    case 'resolved':
      return 'green'
    case 'closed':
      return 'zinc'
  }
}

/**
 * Whether a priority is worth drawing at all - §3.139.
 *
 * A badge on every row is noise: four of five tickets are `normal`, so a marker that always shows tells a
 * reader nothing and costs the width that the franchise name wants. Shown only when it deviates, which is
 * what makes it mean something.
 */
export function priorityNeedsSaying(priority: TicketPriority): boolean {
  return priority !== 'normal'
}

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  bug: tt('Chyba'),
  question: tt('Otázka'),
  feature: tt('Návrh na vylepšenie'),
  billing: tt('Fakturácia'),
  hardware: tt('Hardvér'),
  other: tt('Iné'),
}

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: tt('Nízka'),
  normal: tt('Normálna'),
  high: tt('Vysoká'),
  urgent: tt('Urgentná'),
}

/**
 * The statuses a franchise user may set, which is where the manager's buttons come from.
 *
 * **Mirrors `SupportTicketRules.FRANCHISE_MAY_SET` and the closed rule with it.** A franchise may close its own
 * ticket and may reopen a resolved one; it may not claim `resolved` or `in_progress`, because those are statements
 * about the platform's work. Nothing moves out of `closed`.
 */
export function franchiseMayMoveTo(from: TicketStatus): readonly TicketStatus[] {
  if (from === 'closed') return []
  return TICKET_STATUSES.filter(
    (status) => status !== from && (status === 'open' || status === 'closed'),
  )
}

/**
 * Whether a ticket is still somebody's work, for the badge both apps draw.
 *
 * `waiting_on_franchise` counts as outstanding: it is waiting on a person rather than finished, and counting it as
 * done would make the badge go quiet the moment the platform asked a question - the dishonesty the fifth status
 * exists to remove.
 */
export function isTicketOutstanding(status: TicketStatus): boolean {
  return status === 'open' || status === 'in_progress' || status === 'waiting_on_franchise'
}

/**
 * What the form refuses before it asks the server - §9.510.
 *
 * **Written from the server's rules rather than from the screen's**, which is
 * `agos-an-empty-string-parses-as-zero`'s other half: a client validator derived from what the component happens to
 * do agrees with the component and not with the endpoint. Returns the first problem, so one mistake is one sentence.
 */
export function ticketDraftProblem(draft: {
  subject: string
  body: string
  category: string
  priority: string
}): string | null {
  const subject = draft.subject.trim()
  const body = draft.body.trim()
  if (subject === '') return tt('Zadajte predmet.')
  if (subject.length > TICKET_SUBJECT_MAX) {
    return `${tt('Predmet je príliš dlhý')} (${String(subject.length)}/${String(TICKET_SUBJECT_MAX)})`
  }
  if (body === '') return tt('Opíšte, čo sa stalo.')
  if (body.length > TICKET_BODY_MAX) {
    return `${tt('Popis je príliš dlhý')} (${String(body.length)}/${String(TICKET_BODY_MAX)})`
  }
  if (!(TICKET_CATEGORIES as readonly string[]).includes(draft.category)) {
    return tt('Vyberte kategóriu.')
  }
  if (!(TICKET_PRIORITIES as readonly string[]).includes(draft.priority)) {
    return tt('Vyberte prioritu.')
  }
  return null
}
